"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useUser } from "../../contexts/UserContext";
import { useDispatch } from 'react-redux'

export default function DataTable({
  columns,
  fetchData,
  pageSize = 50,
  extraParams = {},
  height = 500,
  columnVisibility = {},
  isRefresh
}) {
  const parentRef = useRef(null);
  const { user } = useUser();
  const dispatch = useDispatch()

  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const loadMore = React.useCallback(async () => {
    if (loading || !hasMore || !user?.loginhistory?.companyId) return;

    setLoading(true);
    try {
      const res = await dispatch(fetchData({
        page,
        limit: pageSize,
        ...extraParams,
        companyId: user?.loginhistory?.companyId,
        // companyIds: user?.loginhistory?.companyId,
      }));

      const rows = res || [];

      setData((prev) => [...prev, ...rows]);
      setPage((prev) => prev + 1);

      if (rows.length < pageSize) setHasMore(false);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, user?.loginhistory?.companyId, dispatch, fetchData, page, pageSize, extraParams]);

  // Reset and reload when extraParams changes (e.g., search)
  useEffect(() => {
    if (!user?.loginhistory?.companyId) return;

    // Debounce for search parameter
    const timer = setTimeout(() => {
      // Reset state for fresh load
      setData([]);
      setPage(1);
      setHasMore(true);
      setInitialLoadDone(false);

      // Trigger fresh load
      const freshLoad = async () => {
        setLoading(true);
        try {
          const res = await dispatch(fetchData({
            page: 1,
            limit: pageSize,
            ...extraParams,
            companyId: user?.loginhistory?.companyId,
            // companyIds: user?.loginhistory?.companyId,
          }));
          const rows = res || [];
          setData(rows);
          setPage(2);
          setHasMore(rows.length === pageSize);
          setInitialLoadDone(true);
        } catch (error) {
          console.error('Error loading data:', error);
        } finally {
          setLoading(false);
        }
      };

      freshLoad();
    }, 400); // 400ms debounce

    return () => clearTimeout(timer);
  }, [JSON.stringify(extraParams), user?.loginhistory?.companyId, dispatch, fetchData, pageSize]);

  // Load data on mount and when user becomes available (only if not already loaded)
  useEffect(() => {
    if (user?.loginhistory?.companyId && !initialLoadDone && data.length === 0 && !loading) {
      setInitialLoadDone(true);
      loadMore();
    }
  }, [user?.loginhistory?.companyId, initialLoadDone, loadMore, data.length, loading]);

  useEffect(() => {

    const freshLoad = async () => {
      setLoading(true);
      try {
        const res = await dispatch(fetchData({
          page: 1,
          limit: pageSize,
          ...extraParams,
          companyId: user?.loginhistory?.companyId,
          // companyIds: user?.loginhistory?.companyId,
        }));
        const rows = res || [];
        setData(rows);
        setPage(2);
        setHasMore(rows.length === pageSize);
        setInitialLoadDone(true);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isRefresh) {
      freshLoad();
    } else {
      freshLoad();
    }
  }, [isRefresh]);

  const table = useReactTable({
    data,
    columns: columns.filter((column) => columnVisibility[column.id] !== false),
    state: {
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const tableRows = table.getRowModel().rows;

  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 48,
    overscan: 5,
  });

  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;

    const onScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
        loadMore();
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loadMore]);

  return (
    <div
      ref={parentRef}
      className="relative overflow-auto border border-gray-300 rounded-lg bg-white shadow-sm"
      style={{ height }}
    >
      <table className="w-full border-collapse table-fixed">
        {/* HEADER */}
        <thead className="sticky top-0 z-10 bg-gray-100 border-b border-gray-300">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => (
                <th
                  key={header.id}
                  className="px-3 py-2 text-left text-xs font-semibold text-black uppercase tracking-wider border-r border-gray-200 last:border-r-0"
                  style={{ width: `${header.column.columnDef.size}px` || 'auto' }}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        {/* BODY */}
        <tbody
          className="relative bg-white"
          style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = tableRows[virtualRow.index];
            return (
              <tr
                key={row.id}
                className="absolute left-0 right-0 border-b border-gray-200 hover:bg-gray-50 transition-colors"
                style={{
                  transform: `translateY(${virtualRow.start}px)`,
                  height: '48px',
                }}
              >
                {row.getVisibleCells()
                  // .filter((cell) => {
                  //   const columnId = cell.column.id;
                  //   return columnVisibility[columnId] !== false;
                  // })
                  .map((cell) => (
                    <td
                      key={cell.id}
                      className="px-3 py-2 text-sm text-gray-700 border-r border-gray-100 last:border-r-0 text-wrap"
                      style={{ width: `${cell.column.columnDef.size}px` || 'auto' }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* FOOTER STATES */}
      {loading && (
        <div className="sticky bottom-0 py-3 text-center text-sm text-gray-500 bg-white border-t border-gray-200">
          Loading more records...
        </div>
      )}

      {!hasMore && !loading && data.length > 0 && (
        <div className="sticky bottom-0 py-3 text-center text-sm text-gray-400 bg-gray-50 border-t border-gray-200">
          No more records
        </div>
      )}
    </div>
  );
}